import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Select } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ORDERS_PAGE_SIZE, useOrdersQuery } from '../api';
import { STATUS_LABEL_KEY } from '../components/OrderStatusBadge';
import { OrderTable } from '../components/OrderTable';
import { STATUS_TONE_FIGMA, TONE_FILL } from '../status-tone';
import type { OrderStatus } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

/**
 * DEV §9.4's merchant order surface. No client-side gating on
 * `orders:write` before the first fetch — unlike `StaffPage` (gated on
 * `roleKey === 'merchant_owner'`), `OwnMembership` carries no permission
 * list to check ahead of time (confirmed: `features/shops/types.ts`
 * names only `roleKey`). A 403 the API genuinely returns is shown
 * exactly like any other query error below — the server remains the
 * real barrier, the same posture already documented for Staff.
 *
 * Paginated since DT-5 / P1-3 — the same footer + Précédent/Suivant
 * pattern `MerchantsPage` (admin-dashboard, P0-1) already established.
 */
/**
 * The status filter lives in the URL (`/orders?status=PACKED`), so a link — « À traiter », a bookmark, a colleague —
 * opens the list already filtered. An unknown value is ignored (the unfiltered list), never an error.
 */
export function statusFromSearch(raw: string | null): string {
  return raw !== null && Object.hasOwn(STATUS_LABEL_KEY, raw) ? raw : '';
}

/**
 * One-tap filters for the steps of the daily work (« Chechia »: each carries its dye step), beside the full status
 * list. They write the same `?status=` parameter, so the list, a link and a bookmark stay one mechanism.
 */
const QUICK_STATUSES: readonly OrderStatus[] = [
  'PAYMENT_CONFIRMED',
  'PROCESSING',
  'PACKED',
  'SHIPPED',
  'DELIVERED',
  'CANCELLED',
];

export function OrderListPage(): ReactElement {
  const { t, direction } = useI18n();
  const prevArrow = direction === 'rtl' ? '→' : '←';
  const nextArrow = direction === 'rtl' ? '←' : '→';
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = statusFromSearch(searchParams.get('status'));
  const [offset, setOffset] = useState(0);
  const ordersQuery = useOrdersQuery(statusFilter.length > 0 ? statusFilter : undefined, offset);

  // A changed filter is a new query — start it back at the first page.
  useEffect(() => {
    setOffset(0);
  }, [statusFilter]);

  const page = ordersQuery.data;
  const hasPrev = offset > 0;
  const hasNext = page !== undefined && offset + page.orders.length < page.total;

  // The ui-kit Select draws its own empty option (its placeholder): « Tous les statuts » is that option, not a second
  // `''` entry the placeholder would shadow.
  const statusOptions = (Object.keys(STATUS_LABEL_KEY) as readonly OrderStatus[]).map((status) => ({
    value: status,
    label: t(STATUS_LABEL_KEY[status]),
  }));

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="sk-page-title">{t('merchant.orders.title')}</h1>
          <p className="mt-1 text-sm text-inkFaint">{t('merchant.orders.subtitle')}</p>
        </div>
        {/* The filter at its own width beside the main action, on one line (the select fills its box otherwise). */}
        <div className="flex flex-wrap items-center gap-2">
          {/* On a phone the quick chips below are the filter; the full list stays from 640 px up. */}
          <div className="hidden w-52 sm:block">
            <Select
              options={statusOptions}
              placeholder={t('merchant.orders.filterAllStatuses')}
              value={statusFilter}
              onChange={(event) => {
                setSearchParams(event.target.value === '' ? {} : { status: event.target.value }, {
                  replace: true,
                });
              }}
              aria-label={t('merchant.orders.filterAria')}
            />
          </div>
          <Link to="/orders/new">
            <Button>{t('merchant.orders.add')}</Button>
          </Link>
        </div>
      </div>

      {/* The quick filters scroll sideways inside their own row on a phone, never the page. */}
      <div
        className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0"
        role="group"
        aria-label={t('merchant.orders.filterAria')}
      >
        {(['', ...QUICK_STATUSES] as const).map((status) => {
          const selected = statusFilter === status;
          return (
            <button
              key={status === '' ? 'all' : status}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                setSearchParams(status === '' ? {} : { status }, { replace: true });
              }}
              className={`press flex min-h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-[13px] font-medium ${
                selected
                  ? 'border-primary bg-primary text-onPrimary'
                  : 'border-[var(--souk-control-edge)] bg-surface text-inkSoft hover:border-inkFaint hover:text-ink'
              }`}
            >
              {status === '' ? null : (
                <span
                  aria-hidden="true"
                  className={`h-2 w-2 rounded-full ${TONE_FILL[STATUS_TONE_FIGMA[status]]}`}
                />
              )}
              {status === '' ? t('merchant.orders.filterAllStatuses') : t(STATUS_LABEL_KEY[status])}
            </button>
          );
        })}
      </div>

      {ordersQuery.isPending ? (
        <TableSkeleton columns={6} />
      ) : ordersQuery.isError ? (
        <EmptyState
          title={
            ordersQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.orders.loadError')
          }
          description={
            ordersQuery.error.status === 403
              ? t('merchant.orders.accessDeniedDescription')
              : t(resolveErrorMessageKey(ordersQuery.error))
          }
        />
      ) : page === undefined || page.orders.length === 0 ? (
        <EmptyState title={t('merchant.common.noOrdersYet')} />
      ) : (
        <>
          <OrderTable orders={page.orders} />
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-surface px-4 py-3 text-sm text-inkFaint">
            <span>
              {t('merchant.orders.paginationSummary', {
                from: offset + 1,
                to: offset + page.orders.length,
                total: page.total,
              })}
              {page.total > ORDERS_PAGE_SIZE
                ? t('merchant.orders.paginationPageSuffix', {
                    page: Math.floor(offset / ORDERS_PAGE_SIZE) + 1,
                    pages: Math.ceil(page.total / ORDERS_PAGE_SIZE),
                  })
                : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasPrev}
                onClick={() => {
                  setOffset((current) => Math.max(0, current - ORDERS_PAGE_SIZE));
                }}
              >
                {prevArrow} {t('merchant.orders.prev')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!hasNext}
                onClick={() => {
                  setOffset((current) => current + ORDERS_PAGE_SIZE);
                }}
              >
                {t('merchant.orders.next')} {nextArrow}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
